import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { BehaviorSubject, last, take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { addLedger, deleteLedger, updateLedger } from '../common/stores/ledger/ledger.actions';
import { addItem, deleteItem, loadItems, updateItem } from '../common/stores/item/item.actions';
import { RuntimeConfigService } from './runtime-config.service';
import { isPlatform } from '@ionic/angular';
import { Preferences } from '@capacitor/preferences';
import { selectAllItems } from '../common/stores/item/item.selector';
import { selectAllLedgers } from '../common/stores/ledger/ledger.selector';

@Injectable({
  providedIn: 'root'
})
export class SignalrService {

  private headers = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded',
    Accept: '*/*'
  });

  private hubConnection: HubConnection;

  private itemCompanyLedgerUpdateSubject = new BehaviorSubject<string>(null);

  itemCompanyLedgerUpdates$ = this.itemCompanyLedgerUpdateSubject.asObservable();

  private companyId: number = 0;
  private financialYear: string = '';

  constructor(private store: Store, private runtimeConfigService: RuntimeConfigService, private http: HttpClient) { }

  public async startConnection(companyId: string) {
    // Get companyId and financialYear from storage
    if (isPlatform('capacitor')) {
      const companyIdPref = await Preferences.get({ key: 'CompanyID' });
      const financialYearPref = await Preferences.get({ key: 'FinancialYear' });
      this.companyId = companyIdPref.value ? +companyIdPref.value : 0;
      this.financialYear = financialYearPref.value || '';
    } else {
      this.companyId = sessionStorage.getItem('CompanyID') ? +sessionStorage.getItem('CompanyID') : 0;
      this.financialYear = sessionStorage.getItem('FinancialYear') || '';
    }

    const token = sessionStorage.getItem('ctoken');
    this.runtimeConfigService.apiUrl$.pipe(take(1)).subscribe(url => {
      if (this.hubConnection) {
        this.hubConnection.stop();
      }
      // Custom retry policy: retry every 2 seconds
      const customRetryPolicy = {
        nextRetryDelayInMilliseconds: () => 2000
      };
      this.hubConnection = new HubConnectionBuilder()
        .withUrl(`${url}appHub`, { accessTokenFactory: () => token })
        .withAutomaticReconnect(customRetryPolicy)
        .build();

      this.hubConnection.start().then(() => {
        console.log('SignalR Connected');
        this.registerHubMethods();
        this.catchUpSinceLastSeen();
      }).catch((err) => console.log('Error while starting SignalR connection: ' + err));

      this.hubConnection.onclose((error) => {
        console.error('SignalR Disconnected:', error);
      });

      this.hubConnection.onreconnected(() => {
        console.log('Reconnected to SignalR. Rejoining group:', companyId);
        this.catchUpSinceLastSeen();
      });
    });
  }

  // Removed manual reconnect logic; rely on SignalR's automatic reconnect.

  private registerHubMethods() {
    // Listen for ledger updates
    this.hubConnection.on('ReceiveLedgerAdded', (data: any) => {
      console.log('ReceiveLedgerAdded', data);

      this.store.select(selectAllLedgers(this.companyId, this.financialYear)).pipe(take(1)).subscribe(ledgers => {
        if (ledgers.length > 0) {
          const ledgerData = data.ledgerData;
          const existingLedger = ledgers.find(ledger => ledger.ledgerId == ledgerData.ledgerId);
          if (existingLedger == undefined) {
            this.store.dispatch(addLedger({ ledger: ledgerData }));
            localStorage.setItem('changeId', data.changeId);
          }
        }
      });
    });

    this.hubConnection.on('ReceiveLedgerUpdated', (data: any) => {
      this.store.dispatch(updateLedger({ ledger: data.ledgerData, source: 'signalr' }));
      localStorage.setItem('changeId', data.changeId);
    });

    this.hubConnection.on('ReceiveLedgerDeleted', (data: any) => {
      this.store.dispatch(deleteLedger({ ledgerId: data.ledgerId, source: 'signalr' }));
      localStorage.setItem('changeId', data.changeId);
    });

    //Listen for item updates
    this.hubConnection.on('ReceiveItemAdded', (data: any) => {
      this.store.select(selectAllItems(this.companyId, this.financialYear)).pipe(take(1)).subscribe(items => {
        if (items.length > 0) {
          const itemData = data.itemData;
          const existingItem = items.find(item => item.itemID == itemData.itemID);
          if (existingItem == undefined) {
            this.store.dispatch(addItem({ item: itemData }));
            localStorage.setItem('changeId', data.changeId);
          }
        }
      });
    });

    this.hubConnection.on('ReceiveItemUpdated', (data: any) => {
      this.store.dispatch(updateItem({ item: data.itemData, source: 'signalr' }));
      localStorage.setItem('changeId', data.changeId);
    });

    this.hubConnection.on('ReceiveItemDeleted', (data: any) => {
      this.store.dispatch(deleteItem({ itemId: data.itemId, source: 'signalr' }));
      localStorage.setItem('changeId', data.changeId);
    });

    // Listen for item Company Ledger updates
    this.hubConnection.on('ReceiveItemCompanyLedgerUpdate', (message: string) => {
      this.itemCompanyLedgerUpdateSubject.next(message);
    });
  }

  async catchUpSinceLastSeen() {
    let lastChangeId = +localStorage.getItem('changeId');

    this.runtimeConfigService.apiUrl$.pipe(take(1)).subscribe(async (url) => {
      const data = {
        lastChangeId: lastChangeId
      }
      const body = new HttpParams({ fromObject: data });
      const options = { headers: this.headers };
      this.http.post(`${url}SignalRChanges/GetAllSignalRChanges?lastChangeId`, body, options).subscribe(res => {
        // const res = await fetch(`${url}SignalRChanges/GetAllSignalRChanges?lastChangeId=${lastChangeId}`);
        const changes = res as Array<any>;

        if(!changes || changes.length == 0) return;

        changes.forEach(evt => {
          if (evt.changeId <= lastChangeId) return;
          if (evt.type == 'Item') {
            if (evt.operation == 'Insert') {
              this.store.dispatch(addItem({ item: evt.item }));
            } else if (evt.operation == 'Update') {
              this.store.dispatch(updateItem({ item: evt.item, source: 'signalr' }));
            } else this.store.dispatch(deleteItem({ itemId: evt.itemId, source: 'signalr' }));
          } else if (evt.type == 'Ledger') {
            if (evt.operation == 'Insert') {
              this.store.dispatch(addLedger({ ledger: evt.ledger }));
            } else if (evt.operation == 'Update') {
              this.store.dispatch(updateLedger({ ledger: evt.ledger, source: 'signalr' }));
            } else this.store.dispatch(deleteLedger({ ledgerId: evt.ledgerId, source: 'signalr' }));
          }

          lastChangeId = evt.changeId;
        });

        localStorage.setItem('changeId', lastChangeId.toString());
      });
    });
  }

  public checkHubConnection() {
    console.log('Hub ConnectionId: ', this.hubConnection?.connectionId);
    return this.hubConnection?.connectionId ? true : false;
  }

  public async waitForConnection() {
    while (!this.hubConnection || this.hubConnection.state !== 'Connected') {
      console.log('Waiting for SignalR Connection...');
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }

  public stopConnection() {
    this.hubConnection?.stop().then(() => console.log('SignalR Disconnected'));
  }
}
